import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Plus, Trash2, Landmark } from "lucide-react";
import { toast } from "sonner";

let cache = null;
const listeners = new Set();
const fetchBanks = () => api.get("/bank-accounts").then((r) => { cache = r.data || []; listeners.forEach((f) => f(cache)); }).catch((e) => console.error(e));

export const useBankAccounts = () => {
  const [banks, setBanks] = useState(cache || []);
  useEffect(() => {
    listeners.add(setBanks);
    fetchBanks();
    return () => { listeners.delete(setBanks); };
  }, []);
  return banks.filter((b) => b.active !== false);
};

export const BankSelect = ({ value, onChange, testid, className = "" }) => {
  const banks = useBankAccounts();
  if (banks.length === 0) return null;
  return (
    <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
      className={`w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40 ${className}`}>
      <option value="">Select bank account…</option>
      {banks.map((b) => <option key={b.id} value={b.name}>{b.name}{b.account_no ? ` · ${b.account_no.slice(-4)}` : ""}</option>)}
    </select>
  );
};

const field = "h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const empty = { name: "", bank_name: "", account_no: "", ifsc: "", upi_id: "" };

export const BankAccountsManager = () => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const load = () => api.get("/bank-accounts").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim()) return toast.error("Enter an account name");
    try { await api.post("/bank-accounts", form); setForm(empty); toast.success("Bank account added"); load(); fetchBanks(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (b) => {
    if (!window.confirm(`Remove bank account ${b.name}? Past entries keep its name.`)) return;
    await api.delete(`/bank-accounts/${b.id}`); load(); fetchBanks();
  };

  return (
    <div data-testid="bank-accounts-manager" className="rounded-lg border border-border bg-card p-5">
      <h3 className="font-display text-lg flex items-center gap-2 mb-1"><Landmark size={17} /> Company bank accounts</h3>
      <p className="text-xs text-muted-foreground mb-4">Shown as a picker whenever money goes in or out by bank, UPI or QR, and for cash deposits.</p>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-4">
        <input data-testid="bank-name" className={`${field} md:col-span-2`} placeholder="Account name (e.g. HDFC Current)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input data-testid="bank-bankname" className={field} placeholder="Bank" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
        <input data-testid="bank-accno" className={field} placeholder="Account no" value={form.account_no} onChange={(e) => setForm({ ...form, account_no: e.target.value })} />
        <input data-testid="bank-ifsc" className={field} placeholder="IFSC / UPI ID" value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value })} />
        <button data-testid="bank-add" onClick={add} className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm flex items-center justify-center gap-1.5"><Plus size={15} /> Add</button>
      </div>
      {list.length === 0 ? <div className="text-sm text-muted-foreground">No bank accounts yet.</div> : list.map((b) => (
        <div key={b.id} data-testid={`bank-row-${b.id}`} className="flex items-center justify-between border-t border-border py-2 text-sm">
          <span><b>{b.name}</b> <span className="text-muted-foreground">{[b.bank_name, b.account_no, b.ifsc].filter(Boolean).join(" · ")}</span></span>
          <button data-testid={`bank-del-${b.id}`} onClick={() => del(b)} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
        </div>
      ))}
    </div>
  );
};
